import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { changePassword, getAuthProviders } from "../../api/accountApi";
import AccountSessionsPanel from "../../components/account/AccountSessionsPanel";
import StatusBadge from "../../components/midpoint/intelligence/StatusBadge";

function providerLabel(provider) {
  if (provider === "google") {
    return "Google";
  }
  if (provider === "local") {
    return "Local password";
  }

  return provider;
}

const SecurityPage = () => {
  const [providers, setProviders] = useState([]);
  const [hasPasswordLogin, setHasPasswordLogin] = useState(false);
  const [loadingProviders, setLoadingProviders] = useState(true);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setLoadingProviders(true);
        const body = await getAuthProviders();
        if (!cancelled) {
          setProviders(body.providers || []);
          setHasPasswordLogin((body.providers || []).some((row) => row.isPasswordLogin));
        }
      } catch {
        if (!cancelled) {
          setProviders([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingProviders(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleChangePassword = async (event) => {
    event.preventDefault();
    setPasswordError("");
    setPasswordSuccess("");

    if (passwordForm.newPassword.length < 12) {
      setPasswordError("Password must be at least 12 characters.");
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError("New passwords do not match.");
      return;
    }

    try {
      setChangingPassword(true);
      const body = await changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });
      setPasswordSuccess(body.message || "Password changed successfully.");
      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    } catch (error) {
      const errors = error.body?.errors;
      if (Array.isArray(errors) && errors.length) {
        setPasswordError(errors.join(" "));
      } else {
        setPasswordError(error.message || "Unable to change password.");
      }
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <>
      <section className="panel-card account-security-section">
        <h2>Sign-in methods</h2>
        <p className="muted">
          If you see a session you do not recognize, revoke it and reset your password.
        </p>

        {loadingProviders ? <p className="muted">Loading sign-in methods…</p> : null}

        {!loadingProviders && providers.length ? (
          <ul className="account-sign-in-methods">
            {providers.map((provider) => (
              <li key={`${provider.provider}-${provider.providerEmail || ""}`}>
                <div className="account-sign-in-methods__row">
                  <strong>{providerLabel(provider.provider)}</strong>
                  <StatusBadge
                    label={
                      provider.provider === "google"
                        ? "Linked"
                        : provider.isPasswordLogin
                          ? "Enabled"
                          : "Connected"
                    }
                    tone="confirmed"
                  />
                </div>
                <p className="muted">{provider.providerEmail || "—"}</p>
              </li>
            ))}
          </ul>
        ) : null}

        {!loadingProviders && !hasPasswordLogin ? (
          <p className="muted admin-users-help">
            Password login is not enabled for this account. You can continue signing in with Google
            or use{" "}
            <Link to="/forgot-password">password reset</Link> to set a password.
          </p>
        ) : null}
      </section>

      {hasPasswordLogin ? (
        <section className="panel-card account-security-section">
          <h2>Change password</h2>
          <form className="admin-users-form" onSubmit={handleChangePassword}>
            {passwordSuccess ? (
              <p className="auth-login-message" role="status">
                {passwordSuccess}
              </p>
            ) : null}
            {passwordError ? (
              <p className="auth-login-error" role="alert">
                {passwordError}
              </p>
            ) : null}

            <label className="admin-users-field">
              <span>Current password</span>
              <input
                type="password"
                autoComplete="current-password"
                value={passwordForm.currentPassword}
                onChange={(event) =>
                  setPasswordForm((form) => ({ ...form, currentPassword: event.target.value }))
                }
                required
              />
            </label>
            <label className="admin-users-field">
              <span>New password</span>
              <input
                type="password"
                autoComplete="new-password"
                value={passwordForm.newPassword}
                onChange={(event) =>
                  setPasswordForm((form) => ({ ...form, newPassword: event.target.value }))
                }
                minLength={12}
                required
              />
            </label>
            <label className="admin-users-field">
              <span>Confirm new password</span>
              <input
                type="password"
                autoComplete="new-password"
                value={passwordForm.confirmPassword}
                onChange={(event) =>
                  setPasswordForm((form) => ({ ...form, confirmPassword: event.target.value }))
                }
                minLength={12}
                required
              />
            </label>
            <button type="submit" className="report-submit-cta" disabled={changingPassword}>
              {changingPassword ? "Updating…" : "Change password"}
            </button>
          </form>
        </section>
      ) : null}

      <AccountSessionsPanel />
    </>
  );
};

export default SecurityPage;
